/**
 * LocaleGuard — validates /:locale param and syncs it with I18nContext.
 *
 * Behaviour:
 *  - Valid locale (en/ru/he) → setLang(locale) → render <Outlet />
 *  - Invalid or missing locale → redirect to /${detectedLang}/rest-of-path
 */
import { useEffect } from "react";
import { Navigate, Outlet, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "./I18nContext";
import { supportedLangs, defaultLang } from "./loadTranslations";

function detectLang() {
  if (typeof window !== "undefined") {
    const stored = localStorage.getItem("prombiz_lang");
    if (stored && supportedLangs.includes(stored)) return stored;
    const browser = navigator.language?.split("-")[0];
    if (browser && supportedLangs.includes(browser)) return browser;
  }
  return defaultLang;
}

export function LocaleGuard() {
  const { locale } = useParams();
  const { lang, setLang } = useTranslation();
  const location = useLocation();

  const isValid = supportedLangs.includes(locale);

  useEffect(() => {
    if (isValid && locale !== lang) {
      setLang(locale);
    }
  }, [locale, isValid, lang, setLang]);

  if (!isValid) {
    const detected = detectLang();
    // Strip leading /:locale from path if it's an invalid locale word
    // e.g. /xx/dashboard → /en/dashboard, or /xx → /en/
    const pathWithoutLocale = location.pathname.replace(/^\/[^/]+/, "") || "/";
    return <Navigate to={`/${detected}${pathWithoutLocale}${location.search}${location.hash}`} replace />;
  }

  return <Outlet />;
}
